import * as React from "react";
import { AnalyticsTooltipProps } from "../../types/stripe";

/**
 * AnalyticsTooltip Component
 * 
 * A reusable tooltip component designed for analytics interfaces with
 * consistent styling and behavior.
 */
const AnalyticsTooltip: React.FC<AnalyticsTooltipProps> = ({
  content,
  placement = 'top',
  trigger = 'hover',
  delay = 200,
  children,
  className = "",
  id
}) => {
  const [isVisible, setIsVisible] = React.useState(false);
  const [position, setPosition] = React.useState({ x: 0, y: 0 });
  const triggerRef = React.useRef<HTMLDivElement>(null);
  const tooltipRef = React.useRef<HTMLDivElement>(null);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Calculate tooltip position
  const updatePosition = React.useCallback(() => {
    if (!triggerRef.current || !tooltipRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight
    };

    let x = 0;
    let y = 0;

    switch (placement) {
      case 'top':
        x = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);
        y = triggerRect.top - tooltipRect.height - 8;
        break;
      case 'bottom':
        x = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);
        y = triggerRect.bottom + 8;
        break;
      case 'left':
        x = triggerRect.left - tooltipRect.width - 8;
        y = triggerRect.top + (triggerRect.height / 2) - (tooltipRect.height / 2);
        break;
      case 'right':
        x = triggerRect.right + 8;
        y = triggerRect.top + (triggerRect.height / 2) - (tooltipRect.height / 2);
        break;
    }

    // Keep tooltip within viewport
    if (x < 8) x = 8;
    if (x + tooltipRect.width > viewport.width - 8) {
      x = viewport.width - tooltipRect.width - 8;
    }
    if (y < 8) y = 8;
    if (y + tooltipRect.height > viewport.height - 8) {
      y = viewport.height - tooltipRect.height - 8;
    }

    setPosition({ x, y });
  }, [placement]);

  // Show tooltip with delay
  const showTooltip = React.useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, delay);
  }, [delay]);

  // Hide tooltip immediately
  const hideTooltip = React.useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(false);
  }, []);

  // Toggle tooltip for click trigger
  const toggleTooltip = React.useCallback(() => {
    if (isVisible) {
      hideTooltip();
    } else {
      showTooltip();
    }
  }, [isVisible, showTooltip, hideTooltip]);

  // Handle keyboard events
  const handleKeyDown = React.useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      hideTooltip();
    }
  }, [hideTooltip]);

  // Update position when tooltip becomes visible
  React.useEffect(() => {
    if (isVisible) {
      updatePosition();
    }
  }, [isVisible, updatePosition]);

  // Handle window resize
  React.useEffect(() => {
    const handleResize = () => {
      if (isVisible) {
        updatePosition();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isVisible, updatePosition]);

  // Clean up timeout on unmount
  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Handle click outside to close tooltip
  React.useEffect(() => {
    if (trigger === 'click' && isVisible) {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          triggerRef.current &&
          tooltipRef.current &&
          !triggerRef.current.contains(event.target as Node) &&
          !tooltipRef.current.contains(event.target as Node)
        ) {
          hideTooltip();
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [trigger, isVisible, hideTooltip]);

  const triggerProps = React.useMemo(() => {
    const props: Record<string, any> = {};

    if (trigger === 'hover') {
      props.onMouseEnter = showTooltip;
      props.onMouseLeave = hideTooltip;
    } else if (trigger === 'focus') {
      props.onFocus = showTooltip;
      props.onBlur = hideTooltip;
    } else if (trigger === 'click') {
      props.onClick = toggleTooltip;
    }

    props.onKeyDown = handleKeyDown;
    props.tabIndex = trigger === 'focus' ? 0 : undefined;
    props['aria-describedby'] = isVisible ? `tooltip-${id}` : undefined;

    return props;
  }, [trigger, showTooltip, hideTooltip, toggleTooltip, handleKeyDown, isVisible, id]);

  const getArrowStyle = () => {
    const arrowSize = 6;
    
    switch (placement) {
      case 'top':
        return {
          bottom: -arrowSize,
          left: '50%',
          transform: 'translateX(-50%)',
          borderLeft: `${arrowSize}px solid transparent`,
          borderRight: `${arrowSize}px solid transparent`,
          borderTop: `${arrowSize}px solid rgba(0, 0, 0, 0.9)`
        };
      case 'bottom':
        return {
          top: -arrowSize,
          left: '50%',
          transform: 'translateX(-50%)',
          borderLeft: `${arrowSize}px solid transparent`,
          borderRight: `${arrowSize}px solid transparent`,
          borderBottom: `${arrowSize}px solid rgba(0, 0, 0, 0.9)`
        };
      case 'left':
        return {
          right: -arrowSize,
          top: '50%',
          transform: 'translateY(-50%)',
          borderTop: `${arrowSize}px solid transparent`,
          borderBottom: `${arrowSize}px solid transparent`,
          borderLeft: `${arrowSize}px solid rgba(0, 0, 0, 0.9)`
        };
      case 'right':
        return {
          left: -arrowSize,
          top: '50%',
          transform: 'translateY(-50%)',
          borderTop: `${arrowSize}px solid transparent`,
          borderBottom: `${arrowSize}px solid transparent`,
          borderRight: `${arrowSize}px solid rgba(0, 0, 0, 0.9)`
        };
      default:
        return {};
    }
  };

  return (
    <div className={`analytics-tooltip-container ${className}`.trim()} id={id}>
      <div ref={triggerRef} {...triggerProps}>
        {children}
      </div>

      {isVisible && (
        <div
          ref={tooltipRef}
          className="tooltip-overlay"
          style={{
            position: 'fixed',
            left: position.x,
            top: position.y,
            zIndex: 1070
          }}
          role="tooltip"
          id={`tooltip-${id}`}
        >
          <div className="tooltip-content">
            {content}
            <div className="tooltip-arrow" style={getArrowStyle()}></div>
          </div>
        </div>
      )}

      <style>{`
        .analytics-tooltip-container {
          display: inline-block;
        }

        .tooltip-overlay {
          pointer-events: none;
          animation: fadeIn 0.2s ease-out;
        }

        .tooltip-content {
          background: rgba(0, 0, 0, 0.9);
          color: white;
          padding: 8px 12px;
          border-radius: 4px;
          font-size: 0.875rem;
          line-height: 1.4;
          max-width: 300px;
          word-wrap: break-word;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
          position: relative;
        }

        .tooltip-arrow {
          position: absolute;
          width: 0;
          height: 0;
        }

        /* Content formatting */
        .tooltip-content :global(strong),
        .tooltip-content :global(b) {
          font-weight: 600;
        }

        .tooltip-content :global(em),
        .tooltip-content :global(i) {
          font-style: italic;
        }

        .tooltip-content :global(code) {
          background: rgba(255, 255, 255, 0.2);
          padding: 2px 4px;
          border-radius: 2px;
          font-family: 'Courier New', monospace;
          font-size: 0.8em;
        }

        .tooltip-content :global(ul),
        .tooltip-content :global(ol) {
          margin: 0;
          padding-left: 16px;
        }

        .tooltip-content :global(li) {
          margin-bottom: 2px;
        }

        .tooltip-content :global(br) {
          line-height: 1.6;
        }

        /* Accessibility improvements */
        .tooltip-overlay[role="tooltip"] {
          pointer-events: auto;
        }

        /* High contrast mode support */
        @media (prefers-contrast: high) {
          .tooltip-content {
            background: var(--bs-dark);
            border: 2px solid var(--bs-light);
            color: var(--bs-white);
          }
          
          .tooltip-arrow {
            filter: invert(1);
          }
        }

        /* Reduced motion support */
        @media (prefers-reduced-motion: reduce) {
          .tooltip-overlay {
            animation: none;
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        /* Dark theme support */
        @media (prefers-color-scheme: dark) {
          .tooltip-content {
            background: rgba(255, 255, 255, 0.95);
            color: var(--bs-dark);
          }
          
          .tooltip-content :global(code) {
            background: rgba(0, 0, 0, 0.2);
          }
        }
      `}</style>
    </div>
  );
};

AnalyticsTooltip.displayName = 'AnalyticsTooltip';

export default AnalyticsTooltip;